import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ContactSearchResult {
  id: string;
  name: string;
  phone: string;
  entityType: 'contact' | 'lead';
  accountId?: string | null;
}

export function useContactSearch(query: string) {
  return useQuery({
    queryKey: ['contact-search-phone', query],
    queryFn: async (): Promise<ContactSearchResult[]> => {
      const results: ContactSearchResult[] = [];

      // Search contacts
      const { data: contacts } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, phone, account_id')
        .not('phone', 'is', null)
        .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%`)
        .limit(5);

      for (const c of contacts || []) {
        if (c.phone) {
          results.push({
            id: c.id,
            name: [c.first_name, c.last_name].filter(Boolean).join(' '),
            phone: c.phone,
            entityType: 'contact',
            accountId: c.account_id,
          });
        }
      }

      // Search leads (if contacts didn't fill 5)
      if (results.length < 5) {
        const { data: leads } = await supabase
          .from('leads')
          .select('id, name, phone')
          .not('phone', 'is', null)
          .ilike('name', `%${query}%`)
          .limit(5 - results.length);

        for (const l of leads || []) {
          if (l.phone) {
            results.push({
              id: l.id,
              name: l.name || 'Unknown',
              phone: l.phone,
              entityType: 'lead',
            });
          }
        }
      }

      return results;
    },
    enabled: query.length >= 2,
    staleTime: 10_000,
  });
}
